// Keyboard and ARIA for an editable combobox built on `mud-text-input` — audit E7,
// spec 002 T071, WCAG 2.2 SC 2.1.1 (level A) and SC 4.1.2.
//
// AGE has no editable combobox. `mud-select` is one (role=combobox on its
// trigger, aria-activedescendant, ArrowUp/Down/Home/End/Enter/Escape) but its
// options are a static `options` prop and its trigger is a button, so it cannot
// carry a typed query. The two medicine pickers therefore compose `mud-text-input`
// with a popup of their own, and this hook is the semantics that composition
// owes the keyboard user. Both pickers use it; nothing else does.
//
// AGE-BUG(input-no-aria-passthrough) · vendor/age-design/SOURCE_COMMIT 7322a157 —
// `mud-text-input` renders its focusable `<input class="native">` inside its own
// shadow root (`mud-text-input.js` render(), `delegatesFocus: true`) and forwards only
// aria-label / aria-labelledby / aria-describedby / aria-invalid. There is no
// prop for `role`, `aria-expanded`, `aria-autocomplete` or `aria-haspopup`, so
// this hook writes them onto that shadow input directly, after
// `componentOnReady()`. Stencil's vdom never touches attributes absent from its
// own vnode, so they survive re-renders.
//
// The same shadow boundary is why this is the *focus-moves-into-the-listbox*
// variant of the WAI-ARIA combobox pattern rather than the aria-activedescendant
// one. An IDREF resolves only inside its own node tree, and the popup is
// portalled to `document.body` while the focusable input sits in `mud-text-input`'s
// shadow root, so a reference written there is never followed. `aria-controls`
// is written anyway — it is required of the role and it names the listbox
// truthfully — but nothing is built on it; `aria-activedescendant` is not
// written at all, because an active option that assistive technology cannot
// resolve is worse than none. APG allows either variant, and the one that moves
// DOM focus needs no cross-tree reference: the option itself is focused, so the
// option itself is what gets announced.
//
// Measured against axe-core 4.11 on both pickers: the open popup reports no
// violations. Two of its rules bear on this shape and both are satisfied
// deliberately — `aria-required-attr` wants the `aria-controls` above, and
// `scrollable-region-focusable` wants the scroll container to be reachable,
// which is why the consumers put the listbox itself in the tab order.
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type RefObject,
} from 'react';

/** What a key press means to the combobox. Pure, so it can be tested without a DOM. */
export type ComboboxAction =
  | { kind: 'ignore' }
  /** Open the popup; nothing is focused yet because nothing is rendered yet. */
  | { kind: 'open' }
  /** Move DOM focus to the option at this index. */
  | { kind: 'move'; index: number }
  /** Commit the option at this index. */
  | { kind: 'select'; index: number }
  /** Close the popup and put focus back in the field. */
  | { kind: 'close' }
  /** Keep the popup open, put focus back in the field. */
  | { kind: 'return-to-field' }
  /** Keep the popup open, put focus back in the field and append this character. */
  | { kind: 'typeahead'; character: string };

const IGNORE: ComboboxAction = { kind: 'ignore' };

/** Key handling while focus is in the text field. */
export function comboboxFieldAction(
  key: string,
  state: { open: boolean; optionCount: number },
): ComboboxAction {
  if (key === 'Escape') return state.open ? { kind: 'close' } : IGNORE;
  if (key !== 'ArrowDown' && key !== 'ArrowUp') return IGNORE;
  // A closed popup cannot be entered: the panel renders on a query of two
  // characters or more, so the first ArrowDown asks for it and a second one
  // walks into it. Focusing "the first option" of a list that does not exist yet
  // is the bug this avoids.
  if (!state.open) return { kind: 'open' };
  if (state.optionCount === 0) return IGNORE;
  return { kind: 'move', index: key === 'ArrowDown' ? 0 : state.optionCount - 1 };
}

/** Key handling while DOM focus is on one of the options. */
export function comboboxListboxAction(
  key: string,
  state: { activeIndex: number; optionCount: number },
): ComboboxAction {
  const count = state.optionCount;
  if (count === 0) return IGNORE;
  // The listbox is itself in the tab order (it is the scroll container — see the
  // consumers), so focus can arrive on it with no option active yet. Entering the
  // list is then a move to an END of it, not a step from an option that is not
  // there.
  const untouched = state.activeIndex < 0;
  const current = untouched ? 0 : Math.min(state.activeIndex, count - 1);
  switch (key) {
    case 'ArrowDown':
      return { kind: 'move', index: untouched ? 0 : (current + 1) % count };
    case 'ArrowUp':
      if (untouched) return { kind: 'move', index: count - 1 };
      // Past the top the field is the next stop, not the bottom of the list:
      // the field is where the query is edited and where Tab resumes from.
      return current === 0 ? { kind: 'return-to-field' } : { kind: 'move', index: current - 1 };
    case 'Home':
      return { kind: 'move', index: 0 };
    case 'End':
      return { kind: 'move', index: count - 1 };
    case 'Enter':
    case ' ':
      return { kind: 'select', index: current };
    case 'Escape':
      return { kind: 'close' };
    // Tab out of a portalled popup lands nowhere — the panel is a child of
    // <body>, after every other control on the page, which is the 35-Tab detour
    // the audit measured. So Tab shuts the popup and hands focus back to the
    // field, and the next Tab leaves the field in document order.
    case 'Tab':
      return { kind: 'close' };
    default:
      return key.length === 1 ? { kind: 'typeahead', character: key } : IGNORE;
  }
}

/**
 * What the hook needs of the field element, structurally rather than by class:
 * the AGE React wrapper types `mud-text-input`'s ref as the Stencil element, and that
 * class is not assignable to `HTMLElement` under `exactOptionalPropertyTypes`
 * (it declares `ariaLabel` optional where the DOM lib declares it required).
 */
export interface ComboboxFieldElement {
  focus: () => void;
  readonly shadowRoot: ShadowRoot | null;
  componentOnReady?: () => Promise<unknown>;
}

export interface UseComboboxA11yInput {
  /** Whether the popup is actually rendered — this is what `aria-expanded` states. */
  open: boolean;
  /** How many selectable options the popup currently holds. */
  optionCount: number;
  /** The `mud-text-input` host element that owns the text field. */
  fieldRef: RefObject<ComboboxFieldElement | null>;
  /** The `role="listbox"` element. Absent while the popup shows no options. */
  listboxRef: RefObject<HTMLElement | null>;
  onSelect: (index: number) => void;
  onOpen: () => void;
  onClose: () => void;
  /** Appends a character typed while an option was focused. Optional. */
  onTypeahead?: ((character: string) => void) | undefined;
}

export interface UseComboboxA11yResult {
  listboxId: string;
  optionId: (index: number) => string;
  activeIndex: number;
  onFieldKeyDown: (event: ReactKeyboardEvent) => void;
  onListboxKeyDown: (event: ReactKeyboardEvent) => void;
  onOptionFocus: (index: number) => void;
  /** Commits the option at this index and returns focus to the field. */
  selectOption: (index: number) => void;
  /**
   * Whether a focus event on the field should open the popup. False exactly once
   * after Escape or Tab put the focus back there — otherwise dismissing the popup
   * would re-open it on the same gesture.
   */
  shouldOpenOnFieldFocus: () => boolean;
}

export function useComboboxA11y({
  open,
  optionCount,
  fieldRef,
  listboxRef,
  onSelect,
  onOpen,
  onClose,
  onTypeahead,
}: UseComboboxA11yInput): UseComboboxA11yResult {
  const reactId = useId();
  const listboxId = `combobox-listbox-${reactId}`;
  const [activeIndex, setActiveIndex] = useState(-1);
  const activeIndexRef = useRef(-1);
  const suppressOpenOnFocusRef = useRef(false);

  const setActive = useCallback((index: number) => {
    activeIndexRef.current = index;
    setActiveIndex(index);
  }, []);

  // A closed popup has no active option, and neither has one whose results were
  // replaced by a newer search.
  useEffect(() => {
    if (!open) setActive(-1);
  }, [open, setActive]);
  useEffect(() => {
    if (activeIndexRef.current >= optionCount) setActive(-1);
  }, [optionCount, setActive]);

  useEffect(() => {
    let cancelled = false;
    const host = fieldRef.current;
    if (!host) return undefined;
    const apply = (): void => {
      if (cancelled) return;
      const native = host.shadowRoot?.querySelector('input');
      if (!native) return;
      native.setAttribute('role', 'combobox');
      native.setAttribute('aria-autocomplete', 'list');
      native.setAttribute('aria-haspopup', 'listbox');
      native.setAttribute('aria-expanded', open ? 'true' : 'false');
      // `aria-controls` is a required attribute of `role="combobox"`, and it names
      // the listbox truthfully. It cannot RESOLVE from here — an IDREF is scoped
      // to its own node tree and this input lives in `mud-text-input`'s shadow root
      // while the listbox is portalled to `document.body` — which is the second
      // half of why this is the focus-moves-into-the-listbox variant of the
      // pattern: nothing depends on the reference being followed.
      native.setAttribute('aria-controls', listboxId);
    };
    const ready = host.componentOnReady?.();
    if (ready && typeof ready.then === 'function') void ready.then(apply);
    else apply();
    return () => {
      cancelled = true;
    };
  }, [fieldRef, listboxId, open]);

  const optionId = useCallback((index: number) => `${listboxId}-opt-${index}`, [listboxId]);

  const focusOption = useCallback(
    (index: number) => {
      const options = listboxRef.current?.querySelectorAll<HTMLElement>('[role="option"]');
      const target = options?.[index];
      if (!target) return;
      setActive(index);
      target.focus();
    },
    [listboxRef, setActive],
  );

  const returnToField = useCallback(
    (alsoClose: boolean) => {
      const field = fieldRef.current;
      // The reopen guard is armed only when focus is genuinely about to arrive.
      // Escape typed in the field moves nothing, so arming it there would swallow
      // the next real focus instead of the one it was meant for.
      const focusIsElsewhere = (document.activeElement as unknown) !== field;
      if (alsoClose) {
        suppressOpenOnFocusRef.current = focusIsElsewhere;
        onClose();
      }
      setActive(-1);
      if (focusIsElsewhere) field?.focus();
    },
    [fieldRef, onClose, setActive],
  );

  /**
   * Commits an option and puts focus back in the field — the one path for both
   * Enter and a mouse click. Without it a click leaves focus on an option that
   * the very next render unmounts, and focus falls to `<body>`.
   */
  const selectOption = useCallback(
    (index: number) => {
      const field = fieldRef.current;
      const focusIsElsewhere = (document.activeElement as unknown) !== field;
      onSelect(index);
      setActive(-1);
      if (focusIsElsewhere) {
        suppressOpenOnFocusRef.current = true;
        field?.focus();
      }
    },
    [fieldRef, onSelect, setActive],
  );

  const run = useCallback(
    (action: ComboboxAction, event: ReactKeyboardEvent): void => {
      switch (action.kind) {
        case 'ignore':
          return;
        case 'open':
          event.preventDefault();
          onOpen();
          return;
        case 'move':
          event.preventDefault();
          focusOption(action.index);
          return;
        case 'select':
          event.preventDefault();
          selectOption(action.index);
          return;
        case 'close':
          event.preventDefault();
          // An Escape meant for this popup is not an Escape meant for a modal
          // the popup happens to be standing in.
          event.stopPropagation();
          returnToField(true);
          return;
        case 'return-to-field':
          event.preventDefault();
          returnToField(false);
          return;
        case 'typeahead':
          event.preventDefault();
          returnToField(false);
          onTypeahead?.(action.character);
          return;
      }
    },
    [focusOption, onOpen, onTypeahead, returnToField, selectOption],
  );

  const onFieldKeyDown = useCallback(
    (event: ReactKeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      // A portal's events bubble along the React tree, not the DOM one, so a key
      // pressed on an option also arrives at the wrapper this handler sits on.
      // Both handlers would then run and the field's "enter at the first option"
      // would undo the list's "move to the next one" on every ArrowDown.
      if (listboxRef.current?.contains(event.target as Node)) return;
      run(comboboxFieldAction(event.key, { open, optionCount }), event);
    },
    [listboxRef, open, optionCount, run],
  );

  const onListboxKeyDown = useCallback(
    (event: ReactKeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      run(
        comboboxListboxAction(event.key, {
          activeIndex: activeIndexRef.current,
          optionCount,
        }),
        event,
      );
    },
    [optionCount, run],
  );

  const shouldOpenOnFieldFocus = useCallback(() => {
    const suppressed = suppressOpenOnFocusRef.current;
    suppressOpenOnFocusRef.current = false;
    return !suppressed;
  }, []);

  return {
    listboxId,
    optionId,
    activeIndex,
    onFieldKeyDown,
    onListboxKeyDown,
    onOptionFocus: setActive,
    selectOption,
    shouldOpenOnFieldFocus,
  };
}
